import { memo, useState } from 'react';
import { useQueryClient } from 'react-query';
import PropTypes from 'prop-types';
import { loadStripe } from '@stripe/stripe-js';

import { getCards } from 'api';
import Button from 'components/ui-kit/buttons/button';
import InvoicePaymentOptionModal from 'components/invoices/invoice-payment-options-modal';
import InvoiceTipModal from 'components/invoices/invoice-tip-modal';
import config from 'config';

import { useApp, useModal, useToast } from 'hooks';
import {
  createEmptyTipData,
  DEFAULT_PRESET_PERCENTAGES,
  getPresetTipPercentages,
} from 'helpers/payment-display.helper';
import { getApiErrorMessage } from 'helpers/error.helper';

const getCustomerCards = async (portalId) => {
  const result = await getCards({ queryKey: [0, portalId] });
  return result.data;
};

const InvoicePayButtonBase = ({
  invoiceId,
  portalId,
  totalAmount,
  tipBaseAmount,
  invoiceSettingsOverride,
  creditToBeApplied,
  createPaymentSessionAction,
  payWithSavedCardAction,
  redirectUrl,
  invalidateQueriesOnSkippedPayment,
  buttonLabel,
  deposit = false,
  ...restProps
}) => {
  // Same gate the backend applies before actually deducting it - credit never covers a deposit.
  const creditApplied = deposit ? 0 : creditToBeApplied;
  const [isButtonLoading, setButtonLoading] = useState(false);
  const { portal } = useApp();
  const { addErrorToast } = useToast();
  const { showModal } = useModal();
  const queryClient = useQueryClient();

  const proceedWithSession = async (tipData = createEmptyTipData()) => {
    try {
      const fetchResult = await createPaymentSessionAction(portalId, { invoiceId, deposit, ...tipData });
      const response = fetchResult?.data || {};

      if (response.skippedPayment) {
        invalidateQueriesOnSkippedPayment.forEach((key) => queryClient.invalidateQueries(key));
        return;
      }

      const { sessionId, connectedAccountId } = response;

      if (sessionId && connectedAccountId) {
        const stripePromise = loadStripe(config.stripePublicKey, {
          stripeAccount: connectedAccountId,
        });
        const stripe = await stripePromise;

        await stripe.redirectToCheckout({ sessionId });
        return;
      }

      addErrorToast('Company did not connect bank account');
    } catch (error) {
      addErrorToast(getApiErrorMessage(error, 'Something went wrong'));
    } finally {
      setButtonLoading(false);
    }
  };

  const openPaymentOptionsModal = (
    cards,
    tipData = createEmptyTipData(),
    showTipSelector = true,
    presetPercentages = DEFAULT_PRESET_PERCENTAGES,
  ) => {
    showModal(InvoicePaymentOptionModal, {
      cards,
      invoiceId,
      totalAmount,
      tipBaseAmount,
      invoiceSettingsOverride,
      creditApplied,
      tipData,
      isTippingEnabled: showTipSelector && !deposit,
      presetPercentages,
      payWithSavedCardAction,
      redirectUrl,
      deposit,
    });
  };

  const openTipModal = (onContinue, presetPercentages) => {
    showModal(InvoiceTipModal, {
      totalAmount,
      tipBaseAmount,
      creditApplied,
      onContinue,
      presetPercentages,
    });
  };

  const handleButtonClick = async (event) => {
    if (!portalId || !invoiceId) {
      addErrorToast('Something went wrong');
      return;
    }

    event.stopPropagation();
    event.preventDefault();

    setButtonLoading(true);

    const customerCards = await getCustomerCards(portalId);
    const hasCards = !!customerCards.length;
    const tippingSettings = portal?.paymentSettings?.tippingSettings;
    const isTippingEnabled = !!tippingSettings?.allowOnlineTips;
    const useSeparateTipScreen = isTippingEnabled && !!tippingSettings?.useSeparateTipScreen;
    const presetPercentages = getPresetTipPercentages(tippingSettings);

    if (deposit) {
      if (hasCards) {
        openPaymentOptionsModal(customerCards, createEmptyTipData(), false, presetPercentages);
      } else {
        await proceedWithSession(createEmptyTipData());
      }

      setButtonLoading(false);
      return;
    }

    if (!isTippingEnabled) {
      if (hasCards) {
        openPaymentOptionsModal(
          customerCards,
          createEmptyTipData(),
          false,
          presetPercentages,
        );
      } else {
        await proceedWithSession(createEmptyTipData());
      }

      setButtonLoading(false);
      return;
    }

    if (useSeparateTipScreen || !hasCards) {
      openTipModal((tipData) => {
        if (hasCards) {
          // Wait until tip modal close animation completes before opening next modal.
          setTimeout(() => {
            openPaymentOptionsModal(customerCards, tipData, false, presetPercentages);
          }, 320);
          setButtonLoading(false);
          return;
        }

        proceedWithSession(tipData);
      }, presetPercentages);

      setButtonLoading(false);
      return;
    }

    openPaymentOptionsModal(
      customerCards,
      createEmptyTipData(),
      true,
      presetPercentages,
    );

    setButtonLoading(false);
  };

  return (
    <Button
      {...restProps}
      onClick={handleButtonClick}
      isLoading={isButtonLoading}
    >
      {buttonLabel}
    </Button>
  );
};

InvoicePayButtonBase.propTypes = {
  invoiceId: PropTypes.string.isRequired,
  portalId: PropTypes.string.isRequired,
  totalAmount: PropTypes.number.isRequired,
  tipBaseAmount: PropTypes.number,
  invoiceSettingsOverride: PropTypes.shape({
    ccSurchargeOverride: PropTypes.shape({
      enabled: PropTypes.bool.isRequired,
    }),
  }),
  creditToBeApplied: PropTypes.number,
  createPaymentSessionAction: PropTypes.func.isRequired,
  payWithSavedCardAction: PropTypes.func.isRequired,
  redirectUrl: PropTypes.string.isRequired,
  invalidateQueriesOnSkippedPayment: PropTypes.arrayOf(PropTypes.string).isRequired,
  buttonLabel: PropTypes.string,
  deposit: PropTypes.bool,
};

InvoicePayButtonBase.defaultProps = {
  tipBaseAmount: null,
  invoiceSettingsOverride: null,
  creditToBeApplied: 0,
  buttonLabel: 'Pay Invoice',
  deposit: false,
};

export default memo(InvoicePayButtonBase);
